import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Shield, Users, GitBranch, AlertCircle, ShieldCheck } from 'lucide-react';
import { api, endpoints } from '@/lib/api-client';
import { RoleManager, UserRole } from '@/components/security/RoleManager';
import { RoleHierarchyTree, RoleHierarchy, UserRolesList } from '@/components/security/roles';

export const Route = createFileRoute('/_dashboard/admin/org/roles')({
  component: OrgRolesPage,
});

interface CasbinPolicy {
  id: string;
  ptype: string;
  v0: string;
  v1: string;
  v2?: string;
  v3?: string;
  v4?: string;
  v5?: string;
}

function OrgRolesPage() {
  const [assignments, setAssignments] = useState<UserRole[]>([]);
  const [hierarchy, setHierarchy] = useState<RoleHierarchy>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [availableRoles] = useState(['admin', 'developer', 'viewer', 'member', 'billing', 'manager']);

  // Fetch role assignments from Casbin policies
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoading(true);
        const policies = await api.get<CasbinPolicy[]>(endpoints.adminCasbinPolicies);

        // Extract role assignments (g policies: user, role)
        const roleAssignments: UserRole[] = policies
          .filter((p) => p.ptype === 'g')
          .map((p) => ({ user: p.v0, role: p.v1 }));

        // Build hierarchy from g2 policies (role, parent_role)
        const hierarchyData: RoleHierarchy = {};
        const inheritancePolicies = policies.filter((p) => p.ptype === 'g2');

        // Initialize all roles
        const allRoles = new Set<string>();
        roleAssignments.forEach((a) => allRoles.add(a.role));
        inheritancePolicies.forEach((p) => {
          allRoles.add(p.v0);
          allRoles.add(p.v1);
        });

        allRoles.forEach((role) => {
          hierarchyData[role] = {
            name: role,
            users: roleAssignments.filter((a) => a.role === role).map((a) => a.user),
            children: [],
          };
        });

        // Add inheritance relationships
        inheritancePolicies.forEach((p) => {
          if (hierarchyData[p.v1]) {
            hierarchyData[p.v1].children.push(p.v0);
          }
        });

        setAssignments(roleAssignments);
        setHierarchy(hierarchyData);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
        setError('Failed to load role assignments. Please try again.');
        setAssignments([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // Handle assigning a role to a user
  const handleAssignRole = useCallback(async (user: string, role: string) => {
    try {
      await api.post(endpoints.adminCasbinPolicies, {
        ptype: 'g',
        v0: user,
        v1: role,
      });
      setAssignments((prev) => [...prev, { user, role }]);

      // Update hierarchy
      setHierarchy((prev) => {
        const updated = { ...prev };
        if (!updated[role]) {
          updated[role] = { name: role, users: [], children: [] };
        }
        updated[role] = {
          ...updated[role],
          users: [...updated[role].users, user],
        };
        return updated;
      });
    } catch (err) {
      console.error('Failed to assign role:', err);
      setError('Failed to assign role. Please try again.');
    }
  }, []);

  // Handle unassigning a role from a user
  const handleUnassignRole = useCallback(async (user: string, role: string) => {
    try {
      await api.delete(`${endpoints.adminCasbinPolicies}`, {
        data: { ptype: 'g', v0: user, v1: role },
      });
      setAssignments((prev) => prev.filter((a) => !(a.user === user && a.role === role)));

      // Update hierarchy
      setHierarchy((prev) => {
        const updated = { ...prev };
        if (updated[role]) {
          updated[role] = {
            ...updated[role],
            users: updated[role].users.filter((u) => u !== user),
          };
        }
        return updated;
      });
    } catch (err) {
      console.error('Failed to unassign role:', err);
      setError('Failed to unassign role. Please try again.');
    }
  }, []);

  // Handle adding role inheritance
  const handleAddInheritance = useCallback(async (childRole: string, parentRole: string) => {
    try {
      await api.post(endpoints.adminCasbinPolicies, {
        ptype: 'g2',
        v0: childRole,
        v1: parentRole,
      });

      setHierarchy((prev) => {
        const updated = { ...prev };
        if (!updated[parentRole]) {
          updated[parentRole] = { name: parentRole, users: [], children: [] };
        }
        updated[parentRole] = {
          ...updated[parentRole],
          children: [...updated[parentRole].children, childRole],
        };
        return updated;
      });
    } catch (err) {
      console.error('Failed to add inheritance:', err);
      setError('Failed to add role inheritance. Please try again.');
    }
  }, []);

  // Handle removing role inheritance
  const handleRemoveInheritance = useCallback(async (childRole: string, parentRole: string) => {
    try {
      await api.delete(`${endpoints.adminCasbinPolicies}`, {
        data: { ptype: 'g2', v0: childRole, v1: parentRole },
      });

      setHierarchy((prev) => {
        const updated = { ...prev };
        if (updated[parentRole]) {
          updated[parentRole] = {
            ...updated[parentRole],
            children: updated[parentRole].children.filter((c) => c !== childRole),
          };
        }
        return updated;
      });
    } catch (err) {
      console.error('Failed to remove inheritance:', err);
      setError('Failed to remove role inheritance. Please try again.');
    }
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const uniqueRoles = [...new Set(assignments.map((a) => a.role))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Role Management</h1>
          <p className="text-muted-foreground">
            Configure Casbin-based role assignments and hierarchy
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="px-3 py-1 bg-blue-50 text-blue-700 border-blue-200">
            <ShieldCheck className="w-3 h-3 mr-1" /> Casbin Active
          </Badge>
          <Badge variant="secondary" className="px-3 py-1">
            {uniqueRoles.length} roles
          </Badge>
          <Badge variant="secondary" className="px-3 py-1">
            {assignments.length} assignments
          </Badge>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="assignments" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[450px]">
          <TabsTrigger value="assignments">
            <Shield className="w-4 h-4 mr-2" /> Assignments
          </TabsTrigger>
          <TabsTrigger value="hierarchy">
            <GitBranch className="w-4 h-4 mr-2" /> Hierarchy
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" /> Users
          </TabsTrigger>
        </TabsList>

        {/* ASSIGNMENTS TAB */}
        <TabsContent value="assignments" className="mt-6">
          <RoleManager
            assignments={assignments}
            availableRoles={availableRoles}
            onAssign={handleAssignRole}
            onUnassign={handleUnassignRole}
          />
        </TabsContent>

        {/* HIERARCHY TAB */}
        <TabsContent value="hierarchy" className="mt-6">
          <RoleHierarchyTree
            hierarchy={hierarchy}
            roleAssignments={assignments}
            onAddInheritance={handleAddInheritance}
            onRemoveInheritance={handleRemoveInheritance}
            onSelectUser={(user) => setSelectedUser(user)}
          />
        </TabsContent>

        {/* USERS TAB */}
        <TabsContent value="users" className="mt-6">
          <UserRolesList
            assignments={assignments}
            selectedUser={selectedUser}
            onClearSelection={() => setSelectedUser(null)}
            onUnassign={handleUnassignRole}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
